/**
 * Lot Map Selection Header DOM Component
 *
 * Header for the lotMapSelection panel: lot title, plan-count subheading, and lowest-price
 * summary. Renders as an HTML string for direct injection into the panel.
 *
 * No back/close control here by product decision — the user leaves this view by tapping
 * anywhere on the map outside the selected lot (see lotInteraction.plugin.js's
 * onMapBackgroundClicked, which dispatches navigateBack()).
 *
 * @module nativeInteractiveSiteMap/components/lotMapSelectionHeader.dom
 */

import fastdom from 'fastdom';
import { floorPlanIconSvg, attachMoneyIconSvg } from 'constants/svg-assets';

function buildTitle(lotGroupName, displayLabel) {
  const lotPart = displayLabel ? `Lot ${displayLabel}` : 'Lot';
  return lotGroupName ? `${lotPart} - Group ${lotGroupName}` : lotPart;
}

/**
 * Renders the lot map selection header as an HTML string.
 * @param {object} viewModel
 * @param {string|null} viewModel.lotGroupName
 * @param {string|null} viewModel.displayLabel
 * @param {string|null} viewModel.status
 * @param {number} viewModel.planCount
 * @param {string|null} viewModel.lowestFormattedPrice
 * @param {object|null} viewModel.housingUnit - when set, this is the housing-unit case (QMI/
 *   Model) — neither the plan-count subheading nor the price line apply (the QMI card itself
 *   already shows its own price), so the whole details block is omitted, leaving just the title.
 * @returns {string} HTML string for the header
 */
export function renderLotMapSelectionHeader({
  lotGroupName,
  displayLabel,  
  planCount,
  lowestFormattedPrice,
  housingUnit,
}) {
  const title = buildTitle(lotGroupName, displayLabel);
  const planLabel = planCount === 1 ? 'Floor Plan Available' : 'Floor Plans Available';
  const price = lowestFormattedPrice ?? '';
  const detailsHtml = housingUnit ? '' : `
        <div class="nhs-c-native-interactive-site-map__lot-map-selection-subheading nhs-mq-native-interactive-site-map__lot-map-selection-subheading-medium">
          <p data-lot-map-selection-subheading> 
            ${floorPlanIconSvg}
            <span class="nhs-o-typography--sans-body-1-small-regular">${planCount} ${planLabel}</span>
          </p>
          <p data-lot-map-selection-price>
            ${attachMoneyIconSvg}
            <span class="nhs-o-typography--sans-body-1-small-regular">${price}</span>
          </p>
        </div>`;

  return `
    <div class="nhs-c-native-interactive-site-map__listing-header nhs-mq-native-interactive-site-map__listing-header-medium" data-lot-map-selection-header>
      <div class="nhs-c-native-interactive-site-map__lot-map-selection-header">
        <h4 class="nhs-c-native-interactive-site-map__panel-title nhs-mq-native-interactive-site-map__panel-title-medium nhs-o-typography--souvenir-subhead-1-large-light" data-lot-map-selection-title>
          ${title}
        </h4>
        ${detailsHtml}
      </div>
    </div>
  `;
}

/**
 * Factory for the lot map selection header with DOM lifecycle management.
 * Returns an object with getElement/remove methods for compatibility with existing code.
 * @returns {object} Header component with getElement() and remove() methods
 */
export default function createLotMapSelectionHeader() {
  const headerElement = document.createElement('div');

  return {
    getElement() {
      return headerElement;
    },

    /**
     * @param {object} viewModel
     * @param {string|null} viewModel.lotGroupName
     * @param {string|null} viewModel.displayLabel
     * @param {string|null} viewModel.status
     * @param {number} viewModel.planCount
     * @param {string|null} viewModel.lowestFormattedPrice
     */
    setContent(viewModel) {
      fastdom.mutate(() => {
        headerElement.innerHTML = renderLotMapSelectionHeader(viewModel);
      });
    },

    remove() {
      if (!headerElement) {
        return;
      }
      fastdom.mutate(() => {
        headerElement.remove();
      });
    },
  };
}
